import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./ClipPreviewPanel.module.css";

export interface VideoPlayerProps {
  /** Video source URL (proxy preferred, hi-res fallback). Empty = unavailable. */
  src: string;
  /** Optional start time in seconds for ranged playback (subclips). */
  startTime?: number;
  /** Optional end time in seconds for ranged playback (subclips). */
  endTime?: number;
}

/** Format seconds as ``m:ss`` (or ``h:mm:ss`` past an hour). */
const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/**
 * Lightweight HTML5 video player for the Clip Preview panel.
 *
 * - Click the video surface to play / pause.
 * - The controls bar is always visible (play/pause, seek bar, time,
 *   fullscreen). The seek bar supports click-to-seek and drag-to-scrub.
 * - Starts paused; with ``preload="metadata"`` the first frame renders as a
 *   poster-like thumbnail.
 *
 * Reset-on-clip-change is handled by the caller mounting this with a
 * ``key`` of the asset id, so each clip gets a fresh, paused player.
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, startTime, endTime }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  // Apply volume/mute to the underlying media element. Runs after mount (ref
  // is set) and whenever the values change; on clip change the parent remounts
  // this component (keyed by asset id), so the level resets to the default.
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.volume = volume;
      video.muted = muted;
    }
  }, [volume, muted]);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMuted((m) => !m);
  }, []);

  const onVolumeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value);
    setVolume(next);
    // Dragging the slider above zero unmutes; dragging to zero mutes.
    setMuted(next === 0);
  }, []);

  const effectiveVolume = muted ? 0 : volume;
  const volumeIcon =
    effectiveVolume === 0 ? "🔇" : effectiveVolume < 0.5 ? "🔈" : "🔊";

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      // Restart the subclip if we're at/past the out-point.
      if (endTime != null && video.currentTime >= endTime) {
        video.currentTime = startTime ?? 0;
      }
      void video.play();
    } else {
      video.pause();
    }
  }, [startTime, endTime]);

  // Seek by a delta (seconds), clamped to the clip bounds. Backs the global
  // arrow-key controls below.
  const seekBy = useCallback((delta: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    const rangeStart = startTime || 0;
    const rangeEnd = endTime || video.duration;
    const next = Math.min(
      Math.max(rangeStart, video.currentTime + delta),
      rangeEnd
    );
    video.currentTime = next;
    setCurrentTime(next - rangeStart);
  }, [startTime, endTime]);

  // Standard video keyboard controls, active while this player is mounted
  // (i.e. the Clip Preview is showing a video): Space toggles play/pause and
  // the Left/Right arrows seek by 5s. Two guards keep it well-behaved:
  //   - typing in a form field is never hijacked (Space/arrows pass through);
  //   - when focus is already inside the player, Space is left to the
  //     focusable surface / control buttons so it isn't toggled twice.
  useEffect(() => {
    const SEEK_STEP_SECONDS = 5;
    const isEditableTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      if (!el || !el.tagName) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!videoRef.current || isEditableTarget(e.target)) return;
      const insidePlayer = !!containerRef.current?.contains(e.target as Node);
      if (e.key === " " || e.key === "Spacebar") {
        // In-player focus (surface / control buttons) already handles Space.
        if (insidePlayer) return;
        e.preventDefault();
        togglePlay();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        seekBy(-SEEK_STEP_SECONDS);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        seekBy(SEEK_STEP_SECONDS);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [togglePlay, seekBy]);

  // Keyboard activation for the clickable video surface: Enter/Space toggle
  // play/pause, mirroring a native button. preventDefault stops Space from
  // scrolling the page while the surface is focused.
  const onSurfaceKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    },
    [togglePlay]
  );

  // Map a pointer x-position on the track to a time and seek there.
  const seekToClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      const video = videoRef.current;
      if (!track || !video || !Number.isFinite(video.duration)) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - rect.left) / rect.width)
      );
      // Subclip-aware: map ratio to the subclip range, not full video.
      const rangeStart = startTime || 0;
      const rangeEnd = endTime || video.duration;
      const rangeDuration = rangeEnd - rangeStart;
      const time = rangeStart + ratio * rangeDuration;
      video.currentTime = time;
      setCurrentTime(time - rangeStart);
    },
    [startTime, endTime]
  );

  const onTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setDragging(true);
    trackRef.current?.setPointerCapture(e.pointerId);
    seekToClientX(e.clientX);
  };

  const onTrackPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    seekToClientX(e.clientX);
  };

  const onTrackPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    trackRef.current?.releasePointerCapture(e.pointerId);
  };

  const toggleFullscreen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  // No playable source: grey box with an unavailable label, no controls.
  if (!src) {
    return (
      <div className={styles.videoContainer}>
        <div className={styles.thumbnail}>
          <span className={styles.placeholderLabel}>
            Video playback is unavailable
          </span>
        </div>
      </div>
    );
  }

  const progressPct =
    duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <div className={styles.videoContainer} ref={containerRef}>
      <div
        className={styles.thumbnail}
        onClick={togglePlay}
        onKeyDown={onSurfaceKeyDown}
        role="button"
        tabIndex={0}
        aria-label={isPlaying ? "Pause video" : "Play video"}
      >
        <video
          ref={videoRef}
          className={styles.video}
          src={src}
          preload="metadata"
          playsInline
          onLoadedMetadata={(e) => {
            const video = e.currentTarget;
            // When subclip bounds are provided, use them for the range.
            // Clamp to actual video duration in case the metadata overshoots
            // (e.g., TwelveLabs-created subclips may reference time ranges
            // beyond the proxy file's actual length).
            const hasRange = startTime != null || endTime != null;
            const rangeStart = Math.min(startTime ?? 0, video.duration);
            const rangeEnd = Math.min(endTime ?? video.duration, video.duration);
            setDuration(hasRange ? rangeEnd - rangeStart : video.duration);
            // Subclip ranged playback: seek to the in-point on load.
            if (rangeStart > 0) {
              video.currentTime = rangeStart;
            }
          }}
          onTimeUpdate={(e) => {
            if (!dragging) {
              const time = e.currentTarget.currentTime;
              const rangeStart = startTime ?? 0;
              const rangeEnd = endTime ?? e.currentTarget.duration;
              // Subclip ranged playback: pause at the out-point.
              if (endTime != null && time >= rangeEnd) {
                e.currentTarget.pause();
                e.currentTarget.currentTime = rangeEnd;
                setCurrentTime(rangeEnd - rangeStart);
              } else {
                setCurrentTime(time - rangeStart);
              }
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
        {!isPlaying && (
          <div className={styles.playIcon} aria-hidden="true">
            ▶
          </div>
        )}
      </div>
      <div className={styles.videoControls}>
        <button
          type="button"
          className={styles.ctrlButton}
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? "❚❚" : "▶"}
        </button>
        <div
          className={styles.progressTrack}
          ref={trackRef}
          onPointerDown={onTrackPointerDown}
          onPointerMove={onTrackPointerMove}
          onPointerUp={onTrackPointerUp}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration)}
          aria-valuenow={Math.floor(currentTime)}
        >
          <div
            className={styles.progressFilled}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className={styles.time}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <div className={styles.volume}>
          <button
            type="button"
            className={styles.ctrlButton}
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {volumeIcon}
          </button>
          <input
            type="range"
            className={styles.volumeSlider}
            min={0}
            max={1}
            step={0.05}
            value={effectiveVolume}
            onChange={onVolumeInput}
            onClick={(e) => e.stopPropagation()}
            aria-label="Volume"
          />
        </div>
        <button
          type="button"
          className={styles.ctrlButton}
          onClick={toggleFullscreen}
          aria-label="Toggle fullscreen"
        >
          ⛶
        </button>
      </div>
    </div>
  );
};

export default VideoPlayer;
